/**
 * /login + /signup — Shieldz Payments sign-in (V1.7 dark redesign).
 *
 * Imported from the "Shieldz Sign In" Claude Design. Dark theme with an
 * animated WebGL green-glow background, two-up Google/Telegram, a green
 * wallet CTA, and keystore/seed fallback. All auth wiring is preserved:
 *   - Google  → GIS ID-token button (themed dark)
 *   - Telegram→ custom button via Telegram.Login.auth (needs the numeric bot id)
 *   - Wallet  → SIWE (injected provider)
 *   - Keystore/seed → in-browser SIWE
 *   - Email   → passwordless OTP (env-gated, hidden unless VITE_EMAIL_LOGIN=1)
 *
 * Each provider self-hides unless its build-time env is present. A "Last used"
 * badge (localStorage) nudges returning merchants back to their provider.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  hasInjectedWallet,
  isMobileBrowser,
  walletDeepLinks,
  signInWithWallet,
  signInWithSecret,
  completeGoogle,
  completeTelegram,
  startDiscord,
  completeDiscordCallback,
  completeEmailStart,
  completeEmailVerify,
  SignInError,
} from "../lib/session";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const TG_BOT_ID = import.meta.env.VITE_TELEGRAM_BOT_ID as string | undefined;
const DISCORD_CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
const EMAIL_LOGIN_ENABLED = import.meta.env.VITE_EMAIL_LOGIN === "1";
const ANY_SOCIAL = Boolean(GOOGLE_CLIENT_ID || TG_BOT_ID || DISCORD_CLIENT_ID || EMAIL_LOGIN_ENABLED);

const ACCENT = "#16a34a";

type Provider = "google" | "telegram" | "discord" | "wallet" | "keystore" | "email";
const LAST_KEY = "shieldz.lastProvider";
const rememberProvider = (p: Provider) => {
  try {
    localStorage.setItem(LAST_KEY, p);
  } catch {
    /* private mode */
  }
};
const getLastProvider = (): string | null => {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
};

// Scoped styles: keyframes + hover/active states that inline styles can't express.
const STYLES = `
@keyframes sz-spin { to { transform: rotate(360deg); } }
@keyframes sz-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.sz-auth ::selection { background: rgba(34,197,94,0.3); }
.sz-social { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; min-height:48px; padding:0 14px; border-radius:12px; background:#141416; border:1px solid rgba(255,255,255,0.1); color:#fafafa; font-family:inherit; font-size:14px; font-weight:500; cursor:pointer; transition:background .15s, border-color .15s, transform .12s; }
.sz-social:hover { background:#1c1c1f; border-color:rgba(255,255,255,0.2); }
.sz-social:active { transform:translateY(1px); }
.sz-wallet { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; min-height:52px; padding:0 20px; border-radius:12px; background:${ACCENT}; border:none; color:#fff; font-family:inherit; font-size:15px; font-weight:600; cursor:pointer; box-shadow:0 10px 30px -10px rgba(34,197,94,0.6), inset 0 1px 0 rgba(255,255,255,0.18); transition:filter .15s, transform .12s; }
.sz-wallet:hover { filter:brightness(1.08); }
.sz-wallet:active { transform:translateY(1px); }
.sz-wallet:disabled { cursor:default; filter:saturate(0.7) brightness(0.95); }
.sz-ghost { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; min-height:44px; background:transparent; border:none; color:#8b8b94; font-family:inherit; font-size:13px; font-weight:500; cursor:pointer; transition:color .15s; }
.sz-ghost:hover { color:#fafafa; }
.sz-link { color:${ACCENT}; text-decoration:none; font-weight:500; }
.sz-link:hover { text-decoration:underline; }
.sz-input { width:100%; min-height:48px; padding:0 14px; border-radius:12px; background:#0d0d0f; border:1px solid rgba(255,255,255,0.12); color:#fafafa; font-family:inherit; font-size:14px; outline:none; transition:border-color .15s; }
.sz-input:focus { border-color:rgba(34,197,94,0.55); }
`;

interface SignInScreenProps {
  mode: "login" | "signup";
}

export function SignInScreen({ mode }: SignInScreenProps) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [showKeystore, setShowKeystore] = useState(false);
  const [showWalletApps, setShowWalletApps] = useState(false);
  const lastUsed = getLastProvider();
  const isSignup = mode === "signup";

  const done = () => navigate({ to: search.redirect ?? "/" });

  // Discord returns here with ?code&state after the user authorizes. Finish
  // the exchange on mount, then route into the dashboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await completeDiscordCallback();
        if (r && !cancelled) {
          rememberProvider("discord");
          done();
        }
      } catch (e) {
        if (!cancelled) fail(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fail = (e: unknown) =>
    setError(
      e instanceof SignInError
        ? friendlyMessage(e.code, e.message)
        : e instanceof Error
          ? e.message
          : String(e),
    );

  const handleConnect = async () => {
    if (connecting) return;
    // Mobile browsers don't inject a wallet provider. Rather than fail with
    // "no wallet found", offer to reopen this page inside a wallet app's
    // in-app browser (where window.ethereum exists and the flow works).
    if (!hasInjectedWallet() && isMobileBrowser()) {
      setError(null);
      setShowWalletApps(true);
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await signInWithWallet();
      rememberProvider("wallet");
      done();
    } catch (e) {
      fail(e);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div
      className="sz-auth"
      style={{
        minHeight: "100dvh",
        background: "#000",
        color: "#fafafa",
        fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{STYLES}</style>
      <BgCanvas />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.5,
          backgroundImage:
            "radial-gradient(circle at center, rgba(255,255,255,0.025) 0.5px, transparent 0.5px)",
          backgroundSize: "4px 4px",
          WebkitMaskImage:
            "radial-gradient(70% 60% at 50% 38%, #000, transparent 75%)",
          maskImage: "radial-gradient(70% 60% at 50% 38%, #000, transparent 75%)",
        }}
      />

      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(24px, 5vw, 56px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 416,
            animation: "sz-rise 0.55s cubic-bezier(0.16,1,0.3,1) both",
          }}
        >
          {/* Wordmark */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <span
              style={{
                fontFamily: "'Geist', sans-serif",
                fontWeight: 700,
                fontSize: 27,
                letterSpacing: "-0.04em",
                color: "#fafafa",
                lineHeight: 1,
              }}
            >
              Shield<span style={{ color: ACCENT }}>Z</span>
            </span>
          </div>

          <h1
            style={{
              textAlign: "center",
              fontSize: "clamp(25px, 5.5vw, 30px)",
              fontWeight: 600,
              letterSpacing: "-0.025em",
              lineHeight: 1.15,
              margin: "0 0 9px",
              color: "#fafafa",
            }}
          >
            {isSignup ? "Create your Shieldz account" : "Sign in to Shieldz Payments"}
          </h1>
          <p
            style={{
              textAlign: "center",
              fontSize: 14.5,
              color: "#8b8b94",
              margin: "0 0 30px",
            }}
          >
            {isSignup ? (
              <>
                Already on Shieldz?{" "}
                <a className="sz-link" href="/login">
                  Sign in
                </a>
              </>
            ) : (
              <>
                New to Shieldz?{" "}
                <a className="sz-link" href="/signup">
                  Create an account
                </a>
              </>
            )}
          </p>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 18,
                padding: "11px 14px",
                borderRadius: 12,
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: "#fca5a5",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}

          {/* Social row */}
          {ANY_SOCIAL && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    GOOGLE_CLIENT_ID && TG_BOT_ID ? "1fr 1fr" : "1fr",
                  gap: 10,
                }}
              >
                {GOOGLE_CLIENT_ID && (
                  <ProviderSlot badge={lastUsed === "google" ? "Last used" : null}>
                    <GoogleButton onError={fail} onDone={done} />
                  </ProviderSlot>
                )}
                {TG_BOT_ID && (
                  <ProviderSlot badge={lastUsed === "telegram" ? "Last used" : null}>
                    <TelegramButton onError={fail} onDone={done} />
                  </ProviderSlot>
                )}
              </div>
              {DISCORD_CLIENT_ID && (
                <div style={{ marginTop: 10 }}>
                  <ProviderSlot badge={lastUsed === "discord" ? "Last used" : null}>
                    <DiscordButton clientId={DISCORD_CLIENT_ID} onError={fail} />
                  </ProviderSlot>
                </div>
              )}
              {EMAIL_LOGIN_ENABLED && (
                <div style={{ marginTop: 10 }}>
                  <EmailLogin onError={fail} onDone={done} />
                </div>
              )}
            </>
          )}

          <Divider label="or use a crypto wallet" />

          {/* Wallet */}
          <button className="sz-wallet" disabled={connecting} onClick={() => void handleConnect()}>
            {connecting ? (
              <>
                <Spinner />
                <span>Connecting wallet…</span>
              </>
            ) : (
              <>
                <CardIcon />
                <span>{isSignup ? "Connect wallet & sign up" : "Connect wallet & sign in"}</span>
              </>
            )}
          </button>
          <p
            style={{
              textAlign: "center",
              fontSize: 11.5,
              color: "#5a5a63",
              lineHeight: 1.55,
              margin: "12px 4px 0",
            }}
          >
            We'll ask for a free signature, not a transaction. Your wallet doesn't
            move funds and doesn't pay gas.
          </p>

          {/* Mobile: no injected provider, so offer to reopen inside a wallet app */}
          {showWalletApps && (
            <div
              style={{
                marginTop: 14,
                padding: "14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <p style={{ fontSize: 12.5, color: "#8b8b94", lineHeight: 1.5, margin: "0 0 12px" }}>
                Mobile browsers can't reach a wallet directly. Open Shieldz inside
                your wallet app, then tap Connect again.
              </p>
              <div style={{ display: "grid", gap: 8 }}>
                {walletDeepLinks().map((w) => (
                  <a key={w.id} className="sz-ghost" href={w.url} style={{ textDecoration: "none" }}>
                    Open in {w.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Keystore */}
          {showKeystore ? (
            <div style={{ marginTop: 14 }}>
              <KeystoreLogin
                isSignup={isSignup}
                onError={fail}
                onDone={() => {
                  rememberProvider("keystore");
                  done();
                }}
              />
            </div>
          ) : (
            <button
              className="sz-ghost"
              style={{ marginTop: 14 }}
              onClick={() => setShowKeystore(true)}
            >
              <KeyIcon />
              Sign in with a keystore or seed phrase
            </button>
          )}

          {/* Trust strip */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              flexWrap: "wrap",
              marginTop: 30,
              paddingTop: 24,
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <TrustItem icon={<BoltIcon />} label="Wallet-native" />
            <TrustItem icon={<ShieldIcon />} label="Private by design" />
            <TrustItem icon={<CardIcon stroke="#34d399" size={13} />} label="Feeless" />
          </div>

          <p
            style={{
              textAlign: "center",
              fontSize: 11.5,
              color: "#52525b",
              lineHeight: 1.55,
              margin: "26px 0 0",
            }}
          >
            By signing in, you agree to our{" "}
            <a href="https://shieldz.cash/terms" style={legalLink}>
              Terms
            </a>{" "}
            and{" "}
            <a href="https://shieldz.cash/privacy" style={legalLink}>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </main>

      <footer
        style={{ position: "relative", zIndex: 1, padding: "20px 24px 26px", textAlign: "center" }}
      >
        <p style={{ fontSize: 11.5, color: "#3f3f46", margin: 0 }}>
          Shieldz Payments · Feeless · No custody ·{" "}
          <a className="sz-link" href="https://shieldz.cash">
            shieldz.cash
          </a>{" "}
          ·{" "}
          <a className="sz-link" href="https://discord.gg/8AmvrMmvMp" target="_blank" rel="noopener">
            Discord
          </a>
        </p>
      </footer>
    </div>
  );
}

const legalLink: React.CSSProperties = {
  color: "#8b8b94",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
};

// ─── provider slot (badge wrapper) ──────────────────────────────────────
function ProviderSlot({
  badge,
  children,
}: {
  badge: string | null;
  children: React.ReactNode;
}) {
  return (
    <div style={{ position: "relative", display: "flex" }}>
      {badge && (
        <span
          style={{
            position: "absolute",
            top: -9,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            background: "#07140d",
            color: "#5ce08f",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.02em",
            padding: "2px 8px",
            borderRadius: 6,
            border: "1px solid rgba(34,197,94,0.35)",
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}

// ─── Discord (OAuth2 code flow; redirects out then back to this page) ────
function DiscordButton({
  clientId,
  onError,
}: {
  clientId: string;
  onError: (e: unknown) => void;
}) {
  const onClick = () => {
    try {
      startDiscord(clientId);
    } catch (e) {
      onError(e);
    }
  };
  return (
    <button className="sz-social" onClick={onClick}>
      <svg width="18" height="14" viewBox="0 0 127.14 96.36" fill="#5865F2" style={{ flexShrink: 0 }}>
        <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
      </svg>
      Discord
    </button>
  );
}

// ─── Google (custom dark button via GIS token client) ───────────────────
function GoogleButton({ onError, onDone }: { onError: (e: unknown) => void; onDone: () => void }) {
  const clientRef = useRef<{ requestAccessToken: () => void } | null>(null);
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    const init = () => {
      const g = (window as unknown as { google?: any }).google;
      if (!g?.accounts?.oauth2 || cancelled || clientRef.current) return;
      clientRef.current = g.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        callback: (resp: { access_token?: string }) => {
          if (!resp.access_token) return;
          void (async () => {
            try {
              rememberProvider("google");
              await completeGoogle(resp.access_token!);
              onDone();
            } catch (e) {
              onError(e);
            }
          })();
        },
        error_callback: () => {
          /* popup closed / cancelled — stay silent */
        },
      });
    };
    const SRC = "https://accounts.google.com/gsi/client";
    if (document.querySelector(`script[src="${SRC}"]`)) init();
    else {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => init();
      document.head.appendChild(s);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onClick = () => {
    if (!clientRef.current) {
      onError(new Error("Google isn't ready yet — try again in a second."));
      return;
    }
    clientRef.current.requestAccessToken();
  };
  return (
    <button className="sz-social" onClick={onClick}>
      <svg width="17" height="17" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
      </svg>
      Google
    </button>
  );
}

// ─── Telegram (custom dark button via Telegram.Login.auth) ──────────────
function TelegramButton({ onError, onDone }: { onError: (e: unknown) => void; onDone: () => void }) {
  useEffect(() => {
    const SRC = "https://telegram.org/js/telegram-widget.js?22";
    if (!document.querySelector(`script[src="${SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.head.appendChild(s);
    }
  }, []);
  const onClick = () => {
    const TG = (window as unknown as { Telegram?: any }).Telegram;
    if (!TG?.Login?.auth || !TG_BOT_ID) {
      onError(new Error("Telegram isn't ready yet — try again in a second."));
      return;
    }
    TG.Login.auth(
      { bot_id: TG_BOT_ID, request_access: "write" },
      (user: Record<string, unknown> | false) => {
        if (!user) return; // cancelled
        void (async () => {
          try {
            rememberProvider("telegram");
            await completeTelegram(user);
            onDone();
          } catch (e) {
            onError(e);
          }
        })();
      },
    );
  };
  return (
    <button className="sz-social" onClick={onClick}>
      <span style={{ display: "flex", color: "#4aa9e8", flexShrink: 0 }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.07 3.36 19.6 19.73c-.26 1.15-.94 1.44-1.9.9l-5.26-3.88-2.54 2.44c-.28.28-.52.52-1.06.52l.38-5.37L18.7 5.6c.42-.38-.1-.59-.66-.21L5.97 13.1.74 11.46c-1.14-.36-1.16-1.14.24-1.69L21.6 1.79c.95-.35 1.78.21 1.47 1.57z" />
        </svg>
      </span>
      Telegram
    </button>
  );
}

// ─── Email (passwordless, dark, env-gated) ──────────────────────────────
function EmailLogin({ onError, onDone }: { onError: (e: unknown) => void; onDone: () => void }) {
  const [stage, setStage] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await completeEmailStart(email.trim());
      setStage("code");
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    try {
      rememberProvider("email");
      await completeEmailVerify(email.trim(), code.trim());
      onDone();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  if (stage === "email") {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="sz-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@business.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && email.trim() && void send()}
        />
        <button
          className="sz-social"
          style={{ width: "auto", padding: "0 18px", whiteSpace: "nowrap" }}
          disabled={busy || !email.trim()}
          onClick={() => void send()}
        >
          {busy ? "…" : "Continue"}
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input
        className="sz-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="6-digit code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => e.key === "Enter" && code.trim().length === 6 && void verify()}
        style={{ letterSpacing: "0.25em", textAlign: "center" }}
      />
      <button
        className="sz-social"
        style={{ width: "auto", padding: "0 18px", whiteSpace: "nowrap" }}
        disabled={busy || code.trim().length !== 6}
        onClick={() => void verify()}
      >
        {busy ? "…" : "Verify"}
      </button>
    </div>
  );
}

// ─── Keystore / seed sign-in (dark) ─────────────────────────────────────
function KeystoreLogin({
  isSignup,
  onError,
  onDone,
}: {
  isSignup: boolean;
  onError: (e: unknown) => void;
  onDone: () => void;
}) {
  const [tab, setTab] = useState<"file" | "phrase">("file");
  const [keystore, setKeystore] = useState<Record<string, unknown> | null>(null);
  const [fileName, setFileName] = useState("");
  const [phrase, setPhrase] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File | null) => {
    setKeystore(null);
    setFileName("");
    if (!file) return;
    try {
      const json = JSON.parse(await file.text()) as Record<string, unknown>;
      setKeystore(json);
      setFileName(file.name);
    } catch {
      onError(new Error("That file isn't valid keystore JSON."));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "file") {
        if (!keystore) throw new Error("Choose a keystore file first.");
        await signInWithSecret({ keystore, passphrase });
      } else {
        if (!phrase.trim()) throw new Error("Enter your seed phrase.");
        await signInWithSecret({ mnemonic: phrase });
      }
      setPhrase("");
      setPassphrase("");
      onDone();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = tab === "file" ? !!keystore : phrase.trim().length > 0;
  const seg = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: 0,
    background: active ? "#1c1c1f" : "transparent",
    color: active ? "#fafafa" : "#8b8b94",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  });

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        background: "#0d0d0f",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 12,
          padding: 4,
          background: "#141416",
          borderRadius: 10,
        }}
      >
        <button type="button" style={seg(tab === "file")} onClick={() => setTab("file")}>
          Keystore file
        </button>
        <button type="button" style={seg(tab === "phrase")} onClick={() => setTab("phrase")}>
          Seed phrase
        </button>
      </div>

      {tab === "file" ? (
        <>
          <label
            className="sz-social"
            style={{ cursor: "pointer", justifyContent: "flex-start" }}
          >
            <KeyIcon />
            {fileName ? `Selected: ${fileName}` : "Choose keystore file (.json)"}
            <input
              type="file"
              accept="application/json,.json"
              style={{ display: "none" }}
              onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <input
            className="sz-input"
            style={{ marginTop: 10 }}
            type="password"
            autoComplete="off"
            placeholder="Keystore passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
          />
        </>
      ) : (
        <textarea
          className="sz-input"
          rows={3}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          placeholder="Enter your 12-word seed phrase, separated by spaces"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          style={{ padding: 12, resize: "vertical", fontFamily: "'Geist Mono', monospace" }}
        />
      )}

      <p style={{ fontSize: 11.5, color: "#5a5a63", lineHeight: 1.5, margin: "10px 2px 0" }}>
        Decrypted on this device and never uploaded — only the signature is sent.
      </p>

      <button
        className="sz-wallet"
        style={{ marginTop: 12 }}
        disabled={busy || !canSubmit}
        onClick={() => void submit()}
      >
        {busy ? <Spinner /> : <KeyIcon />}
        {isSignup ? "Sign up with keystore" : "Sign in with keystore"}
      </button>
    </div>
  );
}

// ─── bits ───────────────────────────────────────────────────────────────
function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "24px 0" }}>
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
      <span
        style={{
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "#5a5a63",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
    </div>
  );
}

function TrustItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#71717a" }}>
      {icon}
      {label}
    </span>
  );
}

const Spinner = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ animation: "sz-spin 0.7s linear infinite" }}
  >
    <path d="M21 12a9 9 0 1 1-6.22-8.56" />
  </svg>
);
const CardIcon = ({ stroke = "currentColor", size = 18 }: { stroke?: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);
const KeyIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m10.7 12.3 8.3-8.3" />
    <path d="M16 6.5 18.5 9" />
  </svg>
);
const BoltIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// ─── animated WebGL green-glow background (ported from the design) ───────
function BgCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const canvas = ref.current;
    if (!canvas) return;
    let gl: WebGLRenderingContext | null = null;
    try {
      gl = canvas.getContext("webgl", {
        antialias: false,
        depth: false,
        alpha: false,
        powerPreference: "low-power",
      }) as WebGLRenderingContext | null;
    } catch {
      gl = null;
    }
    if (!gl) return;
    const g = gl;

    const vsrc = "attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);}";
    const fsrc = [
      "precision mediump float;",
      "uniform vec2 r;uniform float t;",
      "void main(){",
      "  vec2 uv=gl_FragCoord.xy/r;",
      "  float tt=t*0.05;",
      "  float d1=distance(uv,vec2(0.86+0.05*sin(tt),0.04+0.04*cos(tt*0.8)));",
      "  float d2=distance(uv,vec2(0.04+0.05*cos(tt*0.7),1.02+0.05*sin(tt*0.9)));",
      "  float g1=smoothstep(0.72,0.0,d1);",
      "  float g2=smoothstep(0.80,0.0,d2);",
      "  vec3 c=vec3(0.0);",
      "  c+=vec3(0.13,0.77,0.37)*g1*g1*0.17;",
      "  c+=vec3(0.05,0.72,0.50)*g2*g2*0.11;",
      "  gl_FragColor=vec4(c,1.0);",
      "}",
    ].join("\n");

    const mk = (type: number, src: string) => {
      const s = g.createShader(type)!;
      g.shaderSource(s, src);
      g.compileShader(s);
      return s;
    };
    const prog = g.createProgram()!;
    g.attachShader(prog, mk(g.VERTEX_SHADER, vsrc));
    g.attachShader(prog, mk(g.FRAGMENT_SHADER, fsrc));
    g.linkProgram(prog);
    g.useProgram(prog);

    const buf = g.createBuffer();
    g.bindBuffer(g.ARRAY_BUFFER, buf);
    g.bufferData(g.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), g.STATIC_DRAW);
    const loc = g.getAttribLocation(prog, "p");
    g.enableVertexAttribArray(loc);
    g.vertexAttribPointer(loc, 2, g.FLOAT, false, 0, 0);

    const uR = g.getUniformLocation(prog, "r");
    const uT = g.getUniformLocation(prog, "t");

    const scale = 0.5;
    const resize = () => {
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      canvas.width = Math.max(1, Math.floor(w * scale));
      canvas.height = Math.max(1, Math.floor(h * scale));
      g.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const start = performance.now();
    let last = 0;
    const interval = 1000 / 20;
    let raf = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (now - last < interval) return;
      last = now;
      g.uniform2f(uR, canvas.width, canvas.height);
      g.uniform1f(uT, (now - start) / 1000);
      g.drawArrays(g.TRIANGLES, 0, 3);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      const e = g.getExtension("WEBGL_lose_context");
      if (e) e.loseContext();
    };
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}

function friendlyMessage(code: string, fallback: string): string {
  switch (code) {
    case "user_rejected":
      return "Sign-in cancelled. Tap the button again when you're ready.";
    case "no_wallet":
      return "We couldn't find a browser wallet. Install MetaMask, or use Google / Telegram / a keystore.";
    case "no_account":
      return "Your wallet didn't return an account. Unlock it and try again.";
    case "bad_signature":
    case "signer_mismatch":
      return "The signature didn't match. Try once more.";
    case "nonce_expired":
      return "The sign-in challenge expired. Tap the button again to retry.";
    default:
      return fallback;
  }
}
